import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import axiosInterceptorInstance from "../../../utils/axios.interceptor";

type Country = {
  countryId: number;
  countryName: string;
  isoCode3: string;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const HIDDEN_FIELDS = new Set([
  "confirmation_expiration_date",
  "confirmation_date",
  "status.id",
  "status.class",
  "beneficiary.email",
  "callback_url",
]);

const flattenRow = (row: any, prefix = "", result: any = {}) => {
  Object.entries(row || {}).forEach(([key, value]) => {
    const field = prefix ? `${prefix}.${key}` : key;

    if (
      HIDDEN_FIELDS.has(field) ||
      Array.from(HIDDEN_FIELDS).some((hiddenField) =>
        field.startsWith(`${hiddenField}.`),
      )
    ) {
      return;
    }

    if (value && typeof value === "object" && !Array.isArray(value)) {
      flattenRow(value, field, result);
    } else {
      result[field] = Array.isArray(value) ? value.join(", ") : value;
    }
  });

  return result;
};

const headerFromField = (field: string) =>
  field
    .replace(/\./g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (letter) => letter.toUpperCase());

const DtOneTransactions = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [currency, setCurrency] = useState("USD");
  const [countryIsoCode, setCountryIsoCode] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [vendorReference, setVendorReference] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [searchLoading, setSearchLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    fetch("/data/thacountry.json")
      .then((response) => response.json())
      .then((data) =>
        setCountries(
          [...(data.thacountries || [])].sort((a: Country, b: Country) =>
            a.countryName.localeCompare(b.countryName),
          ),
        ),
      )
      .catch(() => toast.error("Unable to load countries."));
  }, []);

  const loadTransactions = async (
    requestedPage = page,
    requestedPageSize = pageSize,
    isNewSearch = false,
  ) => {
    if (isNewSearch) {
      setRows([]);
      setRowCount(0);
      setHasSearched(false);
      setSearchLoading(true);
    } else {
      setRows([]);
      setGridLoading(true);
    }
    toast.dismiss();

    try {
      const response = await axiosInterceptorInstance.get(
        "/FusionHubCrm/GetDtOneTransactions",
        {
          params: {
            DtOneCurrencyAccount: currency,
            Page: requestedPage + 1,
            PerPage: requestedPageSize,
            ...(countryIsoCode && { CountryIsoCode: countryIsoCode }),
            ...(fromDate && { FromDate: fromDate }),
            ...(mobileNumber.trim() && {
              CreditPartyMobileNumber: mobileNumber.trim(),
            }),
            ...(vendorReference.trim() && {
              VendorTransactionReference: vendorReference.trim(),
            }),
          },
        },
      );

      const records = response.data?.payload ?? [];

      setRows(
        records.map((record: any) => ({
          ...flattenRow(record),
          id: record.id,
        })),
      );
      // The endpoint does not return a total count. Keep the next page enabled
      // when a complete page is returned, and stop on the final partial page.
      setRowCount(
        requestedPage * requestedPageSize +
          records.length +
          (records.length === requestedPageSize ? 1 : 0),
      );
      setHasSearched(true);

      if (records.length === 0) toast.info("No records found.");
    } catch {
      setRows([]);
      setRowCount(0);
    } finally {
      if (isNewSearch) setSearchLoading(false);
      else setGridLoading(false);
    }
  };

  const columns = useMemo<GridColDef[]>(() => {
    if (!rows.length) return [];
    return Object.keys(rows[0])
      .filter((field) => field !== "id")
      .map((field) => ({
        field,
        headerName: headerFromField(field),
        minWidth: 170,
        flex: 1,
        sortable: true,
      }));
  }, [rows]);

  const search = () => {
    if (!currency) {
      toast.error("Please select a currency account.");
      return;
    }
    setPage(0);
    loadTransactions(0, pageSize, true);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="boxShadow">
        <h2 className="sectionTitle">DT One Transactions</h2>
        <div className="boxInner">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth required>
                <InputLabel style={{ background: "#fff", padding: "0 5px" }}>
                  Currency
                </InputLabel>
                <Select
                  value={currency}
                  onChange={(event) => setCurrency(event.target.value)}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel style={{ background: "#fff", padding: "0 5px" }}>
                  Country
                </InputLabel>
                <Select
                  value={countryIsoCode}
                  onChange={(event) => setCountryIsoCode(event.target.value)}
                >
                  <MenuItem value="">All Countries</MenuItem>
                  {countries.map((country) => (
                    <MenuItem key={country.countryId} value={country.isoCode3}>
                      {country.countryName} ({country.isoCode3})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: dayjs().format("YYYY-MM-DD") }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Credit Party Mobile Number"
                placeholder="e.g. +447419365585"
                value={mobileNumber}
                onChange={(event) => setMobileNumber(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Vendor Transaction Reference"
                value={vendorReference}
                onChange={(event) => setVendorReference(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                className="dButton dButton2"
                onClick={search}
                disabled={searchLoading || gridLoading}
                sx={{ height: 56 }}
              >
                {searchLoading ? (
                  <CircularProgress color="inherit" size={28} />
                ) : (
                  "Search"
                )}
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>

      {hasSearched && (rows.length > 0 || gridLoading) && (
        <div style={{ height: 560, width: "100%", marginTop: 16 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={gridLoading}
            pagination
            paginationMode="server"
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(newPage) => {
              setPage(newPage);
              loadTransactions(newPage, pageSize);
            }}
            onPageSizeChange={(newPageSize) => {
              setPage(0);
              setPageSize(newPageSize);
              loadTransactions(0, newPageSize);
            }}
            disableSelectionOnClick
          />
        </div>
      )}
    </motion.div>
  );
};

export default DtOneTransactions;
